import { useCallback, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { FigmaAutoLayout } from './FigmaLayout';
import {
  CameraButton,
  CountdownDock,
  Group1244831442,
  GoldPageRoot,
  Image292Frame,
  PreviewFrameControls,
} from './GoldFigmaComponents';
import { clampVwMax, pxArt, pxH, pxW } from './figmaScale';
import { FIGMA_SPEC } from './figmaSpec';
import { cropDataUrlToGoldFrame } from './goldCapture';
import styles from './GoldCameraPage.module.scss';
import captureBtn from '../../images/gold/captureBtn.png';
import count3 from '../../images/gold/3.png';
import count2 from '../../images/gold/2.png';
import count1 from '../../images/gold/1.png';
import right from '../../images/gold/right.png';

const COUNTDOWN = { 3: count3, 2: count2, 1: count1 };
const F = FIGMA_SPEC.cameraFlow;
const C = FIGMA_SPEC.camera;
const P = FIGMA_SPEC.preview;

const HINT_POSE = (
  <>
    Keep your head
    <br />
    and hands in frame
  </>
);

const HINT_PREVIEW = (
  <>
    Should we continue with the pose
    <br />
    or retake?
  </>
);

function GoldCameraPage({ onPhotoConfirmed }) {
  const [camImg, setCamImg] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [phase, setPhase] = useState('capture');
  const webcamRef = useRef(null);

  const frameAspect = C.frame.w / C.frame.h;
  const videoConstraints = {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    aspectRatio: frameAspect,
    facingMode: 'user',
  };

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;
    try {
      const videoEl = webcamRef.current?.video;
      const cropped = await cropDataUrlToGoldFrame(imageSrc, {
        aspectW: videoEl?.clientWidth || C.frame.w,
        aspectH: videoEl?.clientHeight || C.frame.h,
      });
      setCamImg(cropped);
      setCountdown(null);
      setPhase('preview');
    } catch (e) {
      console.warn('Frame crop failed, using raw capture', e);
      setCamImg(imageSrc);
      setCountdown(null);
      setPhase('preview');
    }
  }, []);

  const startCountdown = () => {
    setPhase('countdown');
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          capture();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRetake = () => {
    setCamImg('');
    setCountdown(null);
    setPhase('capture');
  };

  const handleContinue = () => {
    if (!camImg) return;
    onPhotoConfirmed(
      camImg.replace('data:image/png;base64,', 'data:image/png;name=person.png;base64,')
    );
  };

  const isPreview = phase === 'preview' && camImg;
  const isCountdown = phase === 'countdown' && countdown != null;
  const isCapture = phase === 'capture' && !camImg;

  const headerPhase = isPreview ? 'preview' : 'capture';

  const title = isPreview
    ? P.title.text
    : isCountdown
      ? 'Hold Your\u00a0Pose'
      : "Let's Snap!";

  const hint = isPreview ? HINT_PREVIEW : HINT_POSE;

  return (
    <GoldPageRoot bg="camera">
      <FigmaAutoLayout
        direction="vertical"
        align="center"
        className={styles.slide142}
        style={{
          height: '100%',
          paddingTop: pxH(F.headerTop),
          overflow: 'visible',
        }}
      >
        <Group1244831442
          title={title}
          hint={hint}
          phase={headerPhase}
          hideTitle={!isPreview}
        />

        <div
          className={styles.frameStage}
          style={{
            marginTop: pxH(F.frameMarginTop),
            width: clampVwMax(C.frame.w),
            paddingBottom: pxH(F.captureOverlap),
          }}
        >
          <Image292Frame width="100%">
            {isPreview ? (
              <img src={camImg} alt="" className={styles.previewImg} />
            ) : (
              <Webcam
                ref={webcamRef}
                mirrored
                forceScreenshotSourceSize
                screenshotFormat="image/jpeg"
                screenshotQuality={0.98}
                videoConstraints={videoConstraints}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Image292Frame>

          <div
            className={styles.actionDock}
            style={{
              minHeight: pxH(F.actionDockH),
              marginTop: pxH(-F.captureOverlap),
            }}
          >
            {isCapture || isCountdown ? (
              <div
                className={styles.actionDockSlot}
                style={{
                  width: pxArt(C.capture.w),
                  height: pxArt(C.capture.h),
                }}
              >
                {isCapture ? (
                  <CameraButton src={captureBtn} onClick={startCountdown} />
                ) : null}
                {isCountdown && COUNTDOWN[countdown] ? (
                  <CountdownDock src={COUNTDOWN[countdown]} />
                ) : null}
              </div>
            ) : null}
            {isPreview ? (
              <PreviewFrameControls
                onRetake={handleRetake}
                onContinue={handleContinue}
                arrowSrc={right}
              />
            ) : null}
          </div>
        </div>
      </FigmaAutoLayout>
    </GoldPageRoot>
  );
}

export default GoldCameraPage;
